import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";

import { cloneDeep } from "lodash";
import * as yup from "yup";
import { AppDrawer, AppDrawerProps } from "Components/Generic/AppDrawer";
import { useTranslation } from "react-i18next";
import {
  DrawerMultipleOptions,
  DrawerMultipleOptionsLabels,
  DrawerOptionsWrapper,
  ErrorIcon,
  ErrorDisplay,
  OptionsContainer,
  OptionsInputLabel,
  RemoveOptionButton,
  RequiredLabel,
  WrapperFooter,
} from "./styles";
import { Button, Checkbox, Input } from "Components/Form";
import { Color } from "Components/Form/Button";
import { Add, Trash } from "iconsax-react";
import { Option, Questions } from "Models/checklist";
import { ChecklistContext } from "../../../context";
import { ObjectShape } from "yup/lib/object";
import useYupValidation from "Hooks/useYupValidation";
import { QuestionTypes } from "../../../../utils";

export interface OptionsDrawerHandles {
  open: (question: Questions, optionsType: string) => void;
  close: () => void;
}

export const OptionsDrawer = forwardRef<OptionsDrawerHandles>((_, ref) => {
  const { handleUpdateQuestion } = useContext(ChecklistContext);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [question, setQuestion] = useState<Questions>({} as Questions);
  const [optionsType, setOptionsType] = useState<string>(QuestionTypes.SINGLE);
  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<boolean>(false);
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [drawerOptions, setDrawerOptions] = useState<Option[]>([]);

  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  const getMultipleErrorField = (field: string) => {
    const findErrors = errors?.find((error) => error?.path?.includes(field));

    if (findErrors) return true;

    return false;
  };

  const validDrawerOptions = useMemo(() => {
    return drawerOptions?.filter((option) => option.valid) || [];
  }, [drawerOptions]);

  const { t: translate } = useTranslation();

  const handleOpen = (
    questionToUpdateOptions: Questions,
    checklistOptionsType: string
  ) => {
    const newQuestionOptions = [...(questionToUpdateOptions.options || [])];

    const validQuestionOptions =
      questionToUpdateOptions.options?.filter((option) => option.valid) || [];

    if (checklistOptionsType === QuestionTypes.SINGLE) {
      if (Number(validQuestionOptions?.length) === 1) {
        newQuestionOptions.push({ name: "", valid: true, checked: false });
      }

      if (!validQuestionOptions?.length) {
        newQuestionOptions.push(
          { name: "", valid: true, checked: false },
          { name: "", valid: true, checked: false }
        );
      }
    }

    if (checklistOptionsType === QuestionTypes.MULTIPLE && !validQuestionOptions.length) {
      newQuestionOptions.push({ name: "", valid: true, checked: false });
    }

    setDrawerOptions(newQuestionOptions);
    setOptionsType(checklistOptionsType);
    setQuestion(questionToUpdateOptions);
    setIsOpen(true);
  };

  const handleClose = () => {
    setErrors([]);
    setHasFormBeenSubmitted(false);
    setIsOpen(false);
  };

  useImperativeHandle(ref, () => ({
    open: handleOpen,
    close: handleClose,
  }));

  if (!question) return null;

  const { id: questionId } = question;

  const onAddOption = () => {
    setDrawerOptions([
      ...drawerOptions,
      { name: "", question_id: questionId, valid: true, checked: false },
    ]);
  };

  const onUpdateOption = (optionIndex: number, value: string | boolean, attribute: string) => {
    const options = cloneDeep(drawerOptions);

    if (Object.prototype.hasOwnProperty.call(options[optionIndex], attribute)) {
      (options[optionIndex] as any)[attribute] = value;
    }

    setDrawerOptions(options);
  };

  const onDeleteOption = (optionIndex: number) => {
    const options = cloneDeep(drawerOptions);
    options[optionIndex].valid = false;

    setDrawerOptions(options);
  };

  const singleOptionSchema = yup.object().shape({
    options: yup
      .array()
      .min(2, translate("youShouldAddAtLeastTwoOptions"))
      .of(
        yup.object().shape({
          name: yup
            .string()
            .required(translate("allOptionsShouldHaveAName"))
            .typeError(translate("allOptionsShouldHaveAName")),
        })
      ),
  });

  const multipleOptionsSchema = yup.object().shape({
    options: yup
      .array()
      .min(1, translate("youShouldAddAtLeastOneOption"))
      .of(
        yup.object().shape({
          name: yup
            .string()
            .required(translate("allOptionsShouldHaveAName"))
            .typeError(translate("allOptionsShouldHaveAName")),
        })
      ),
  });

  const handleValidateSchemaSuccess = (schema: yup.ObjectSchema<ObjectShape>) => {
    schema.validateSync(
      {
        options: validDrawerOptions,
      },
      { abortEarly: false }
    );

    setErrors([]);

    return true;
  };

  const handleValidateSchemaError = (error: yup.ValidationError): boolean => {
    if (error) {
      setErrors(error?.inner);
    }

    return false;
  };

  const handleValidate = (schema: yup.ObjectSchema<ObjectShape>) => {
    try {
      return handleValidateSchemaSuccess(schema);
    } catch (error) {
      if (error instanceof yup.ValidationError) return handleValidateSchemaError(error);

      return false;
    }
  };

  const handleSave = () => {
    if (!hasFormBeenSubmitted) setHasFormBeenSubmitted(true);

    if (
      handleValidate(
        optionsType === QuestionTypes.MULTIPLE ? multipleOptionsSchema : singleOptionSchema
      )
    ) {
      handleUpdateQuestion({
        value: drawerOptions,
        field: "options",
        temporaryId: question.temporaryId,
        questionId: question.id,
      });

      handleClose();
    }
  };

  useEffect(() => {
    if (hasFormBeenSubmitted) {
      handleValidate(
        optionsType === QuestionTypes.MULTIPLE ? multipleOptionsSchema : singleOptionSchema
      );
    }
  }, [hasFormBeenSubmitted, drawerOptions, optionsType]);

  const drawerContent = () => {
    let optionsCounter = 0;

    if (optionsType === QuestionTypes.MULTIPLE) {
      return (
        <>
          <DrawerMultipleOptionsLabels>
            <OptionsInputLabel>{translate("defaultState")}</OptionsInputLabel>
            <OptionsInputLabel>
              {translate("enterOptions")}
              <RequiredLabel>{"*"}</RequiredLabel>
            </OptionsInputLabel>
          </DrawerMultipleOptionsLabels>

          <DrawerOptionsWrapper>
            {drawerOptions?.map((option, index) => {
              if (!option.valid) return;

              optionsCounter += 1;

              return (
                <DrawerMultipleOptions key={index}>
                  <Checkbox
                    checked={option.checked}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      onUpdateOption(index, event.target.checked, "checked");
                    }}
                  />
                  <Input
                    name="title"
                    placeholder={`${translate("option")} ${optionsCounter}`}
                    required
                    value={option.name}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      onUpdateOption(index, event.target.value, "name");
                    }}
                  />

                  <RemoveOptionButton
                    disabled={validDrawerOptions?.length <= 1}
                    onClick={() => onDeleteOption(index)}
                  >
                    <Trash />
                  </RemoveOptionButton>
                </DrawerMultipleOptions>
              );
            })}
          </DrawerOptionsWrapper>

          <Button
            customColor={Color.GRAY_FILLED}
            onClick={() => onAddOption()}
            startIcon={<Add size={18} />}
          >
            {translate("addOption")}
          </Button>

          {fieldHasError("options") && (
            <ErrorDisplay>
              <ErrorIcon />
              {getErrorByField("options")}
            </ErrorDisplay>
          )}
          {getMultipleErrorField("name") && (
            <ErrorDisplay>
              <ErrorIcon />
              {translate("allOptionsShouldHaveAName")}
            </ErrorDisplay>
          )}
        </>
      );
    }

    return (
      <>
        <OptionsInputLabel>
          {translate("enterOptions")}
          <RequiredLabel>{"*"}</RequiredLabel>
        </OptionsInputLabel>

        <DrawerOptionsWrapper>
          {drawerOptions?.map((option, index) => {
            if (!option.valid) return;

            optionsCounter += 1;

            return (
              <OptionsContainer key={index}>
                <Input
                  name="title"
                  placeholder={`${translate("option")} ${optionsCounter}`}
                  required
                  value={option.name}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    onUpdateOption(index, event.target.value, "name");
                  }}
                />

                <RemoveOptionButton
                  disabled={validDrawerOptions?.length <= 2}
                  onClick={() => onDeleteOption(index)}
                >
                  <Trash />
                </RemoveOptionButton>
              </OptionsContainer>
            );
          })}
        </DrawerOptionsWrapper>

        <Button
          customColor={Color.GRAY_FILLED}
          onClick={() => onAddOption()}
          startIcon={<Add size={18} />}
        >
          {translate("addOption")}
        </Button>

        {fieldHasError("options") && (
          <ErrorDisplay>
            <ErrorIcon />
            {getErrorByField("options")}
          </ErrorDisplay>
        )}
        {getMultipleErrorField("name") && (
          <ErrorDisplay>
            <ErrorIcon />
            {translate("allOptionsShouldHaveAName")}
          </ErrorDisplay>
        )}
      </>
    );
  };

  const drawerSections: AppDrawerProps["sectionItems"] = [
    {
      id: "singleOptions",
      content: drawerContent(),
    },
  ];

  return (
    <AppDrawer
      DrawerProps={{
        open: isOpen,
        onClose: handleClose,
      }}
      title={
        optionsType === QuestionTypes.MULTIPLE
          ? translate("multipleChoiceQuestion")
          : translate("singleChoiceQuestion")
      }
      hideSideMenu
      footer={
        <WrapperFooter>
          <Button onClick={handleClose}>{translate("cancel")}</Button>
          <Button customColor={Color.BRAND} onClick={() => handleSave()}>
            {translate("save")}
          </Button>
        </WrapperFooter>
      }
      sectionItems={drawerSections}
    />
  );
});
